import { Tooltip as T } from "radix-ui";
import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { Kbd } from "./Kbd";

export interface TooltipProps {
  content: ReactNode;
  /** Keyboard shortcut shown after the label, for example "M" or "mod+shift+h". */
  shortcut?: string;
  side?: "top" | "right" | "bottom" | "left";
  children: ReactNode;
  className?: string;
}

/** Wrap the app once. 450 ms open delay so tooltips never flicker during normal pointer travel. */
export function TooltipProvider({ children }: { children: ReactNode }) {
  return (
    <T.Provider delayDuration={450} skipDelayDuration={200}>
      {children}
    </T.Provider>
  );
}

export function Tooltip({ content, shortcut, side = "top", children, className }: TooltipProps) {
  // Each tooltip carries its own provider so it works without app-level setup.
  return (
    <T.Provider delayDuration={450} skipDelayDuration={200}>
      <T.Root>
        <T.Trigger asChild>{children}</T.Trigger>
        <T.Portal>
          <T.Content
            side={side}
            sideOffset={6}
            collisionPadding={8}
            className={cn(
              "z-50 flex items-center gap-2 rounded-sm bg-overlay px-2 py-1 text-caption text-ink shadow-popover",
              "data-[state=delayed-open]:animate-vd-pop-in data-[state=instant-open]:animate-vd-fade-in data-[state=closed]:animate-vd-fade-out",
              className,
            )}
          >
            <span>{content}</span>
            {shortcut ? <Kbd keys={shortcut} size="sm" /> : null}
          </T.Content>
        </T.Portal>
      </T.Root>
    </T.Provider>
  );
}
